import React from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const WorkingThroughConflictStage2 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Scenario 2: Inter-team friction" icon={<UsersIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* TL;DR Section */}
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <p className="mb-4">You need to do 3 things:</p>
          <ol className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Acknowledge the problem</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Find the root cause</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Co-create solution</span>
            </li>
          </ol>
          <p className="mt-4">Here's what we mean:</p>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Acknowledge the problem */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Acknowledge the problem
        </h3>
        <p className="text-gray-300 mb-6">
          The thing to remember about inter-team friction is that you're probably not the first to know about it. There's a good chance this has been a problem for a while, and now it's only coming to a head because the symptoms are inescapably loud.
        </p>
        <p className="text-gray-300 mb-6">
          You have to bear that in mind when you approach:
        </p>
        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300 font-medium">
            Acknowledging the problem.
          </p>
        </div>
        <p className="text-gray-300 mb-6">
          We're going to use a scenario here to make this as real as possible 👇
        </p>
        
        <ExpandableCallout type="warning" title="Scenario: Sales and Marketing hate each other…" className="mb-6">
          <p className="text-gray-300">
            You can cut the tension with a knife when these two areas of the business interact or join a meeting together. It's blame game, finger pointing, passive aggression on overdrive. Sound familiar?
          </p>
        </ExpandableCallout>

        <h4 className="text-lg font-semibold text-white mb-3">
          Where and how to acknowledge the problem?
        </h4>
        <p className="text-gray-300 italic mb-4">
          This depends on what alerted you to the problem. Here are some likely alerts:
        </p>

        <div className="space-y-4 mb-6">
          <div className="flex items-start">
            <span className="text-primary-light font-bold mr-3">—</span>
            <div>
              <p className="text-white font-medium">Alert #1:</p>
              <p className="text-gray-300">If you've had individuals come to you / mention in 121s that x team and y team aren't working together, they're disrupting things for the rest of us</p>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-primary-light font-bold mr-3">—</span>
            <div>
              <p className="text-white font-medium">Alert #2:</p>
              <p className="text-gray-300">It's been discussed at senior leadership team meeting or equivalent. "This is a problem. We need to fix this"</p>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-primary-light font-bold mr-3">—</span>
            <div>
              <p className="text-white font-medium">Alert #3:</p>
              <p className="text-gray-300">You're picking up on this from feedback (e.g. pulse/survey/sli.do comments/questions at town halls</p>
            </div>
          </div>
          <div className="flex items-start">
            <span className="text-primary-light font-bold mr-3">—</span>
            <div>
              <p className="text-white font-medium">Alert #4:</p>
              <p className="text-gray-300">You've personally noticed this in meetings/interactions with the respective teams</p>
            </div>
          </div>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">
          Here's a simple grid to help you:
        </h4>
        
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full bg-gray-800/30 rounded-lg border border-gray-700/50">
            <thead>
              <tr className="border-b border-gray-700/50 bg-gray-800/70">
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">The alert came from….</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">Who to get acknowledgment from</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-white">How</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              <tr>
                <td className="py-3 px-4 text-gray-300">Alert #1: Individual feedback</td>
                <td className="py-3 px-4 text-gray-300">1-2-2 with the leader of each team (e.g. VP Sales + VP Marketing</td>
                <td className="py-3 px-4 text-gray-300">🤙 Call</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-300">Alert #2: Senior leadership team</td>
                <td className="py-3 px-4 text-gray-300">Each team individually (See next step: Find the root cause of the problem)</td>
                <td className="py-3 px-4 text-gray-300">🎨 Workshop individually with each team</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-300">Alert #3: Your own data</td>
                <td className="py-3 px-4 text-gray-300">1-2-2 with the leader of each team (e.g. VP Sales + VP Marketing</td>
                <td className="py-3 px-4 text-gray-300">🤙 Call</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-gray-300">Alert #4: Your own observation</td>
                <td className="py-3 px-4 text-gray-300">1-2-2 with the leader of each team (e.g. VP Sales + VP Marketing</td>
                <td className="py-3 px-4 text-gray-300">🤙 Call</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">
          In each instance we are trying to get one thing:
        </h4>
        <div className="mb-6 flex items-start">
          <span className="text-primary-light font-bold mr-3">—</span>
          <div>
            <p className="text-white">Acknowledgement from <strong>everyone</strong> else that:</p>
            <p className="text-gray-300 mt-1">a) this is a problem, and</p>
            <p className="text-gray-300">b) we need to fix this.</p>
          </div>
        </div>
        <p className="text-gray-300">
          If you have that, then it's time to find and fix 👇
        </p>
      </PlaybookCard>

      {/* Find the root cause */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Find the root cause of the problem
        </h3>
        <p className="text-gray-300 mb-6">
          Permission to fix it achieved ✅
        </p>
        <p className="text-gray-300 mb-6">
          Now we workshop.
        </p>
        <p className="text-gray-300 mb-6">
          We need to understand the REAL root cause of the problem
        </p>

        <ExpandableCallout type="info" title="Tip" className="mb-6">
          <p className="text-gray-300 mb-2">
            In my experience team&lt;&gt;team conflict is often rooted in either:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-gray-300">
            <li>A toxic Individual issue e.g. #BrilliantJerk</li>
            <li>A systemic problem with the way the teams are incentivised or set goals</li>
            <li>A problem between the leaders of the teams/functions (which also tends to be driven either by a personal feud for power or a problem in the way their incentives are structured.</li>
          </ol>
        </ExpandableCallout>

        <p className="text-gray-300 mb-6">
          For psychological safety and honest contributions I tend to run team workshops without the leaders and separate to one another.
        </p>
        <p className="text-gray-300 mb-6">
          I ask permission to do this for exactly these reasons.
        </p>
        <p className="text-gray-300 mb-6">
          Here's how to run the workshop:
        </p>

        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
          <span className="text-primary-light mr-2">⚡</span>
          Action: Facilitation guide
        </h4>

        <ol className="list-decimal pl-6 space-y-6 text-gray-300 mb-6">
          <li>
            <p>Memo to the individual team (do this twice for each team, and best if comes from the team leader e.g. VP Sales and you).</p>
            
            <CopyToNotion title="Invite" notion={true}>
              <p className="text-gray-300">
                Hey folks,
              </p>
              <p className="text-gray-300 mt-2">
                We know from {'{insert data source/event}'} that friction with {'{the other team}'} is preventing us from doing our best work together.
              </p>
              <p className="text-gray-300 mt-2">
                This isn't where we want to be for any longer.
              </p>
              <p className="text-gray-300 mt-2">
                Keen to hear your thoughts on this, so we're going to have a session together using a miro board/whiteboard {'{insert data and time}'}
              </p>
              <p className="text-gray-300 mt-2">
                Any questions please speak to me or {'{your name}'}
              </p>
              <p className="text-gray-300 mt-2">
                - VP Sales
              </p>
            </CopyToNotion>
          </li>
          <li>
            <p className="mb-4">Running the workshop:</p>
            
            <ExpandableCallout type="info" title="Tip" className="mb-4">
              <p className="text-gray-300">
                Might be worth talking 121 with some of the team members - if there are dominant outspoken voices you want to pick up on these beforehand and understand the power dynamics - is it whole team vs whole team or is it a problem between 2 influential individuals
              </p>
            </ExpandableCallout>
            
            <p className="font-medium text-white mb-2">Approach</p>
            <ol className="list-alpha pl-6 space-y-2">
              <li>Make it a safe space: "We want to understand everyone's perspective on why we're having this {'{problem}'} with {'{other team}'}"</li>
              <li>Introduce the format. You can have an open conversation or add some structure. 2 of my favourite ways to do this are:</li>
            </ol>
          </li>
        </ol>

        <div className="mb-6">
          <p className="text-white font-semibold mb-4 text-center">5 whys</p>
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/working-conflict/root-cause.png" 
            alt="5 whys example diagram" 
            className="rounded-lg border border-gray-700/50 mb-4"
          />
          <div className="flex justify-center">
            <ToolCallout
              tool="miro"
              label="5 whys [miro template]"
              href="https://miro.com/templates/5-whys/"
              description="A template for digging deeper into root causes"
            />
          </div>
        </div>

        <div className="mb-6">
          <p className="text-white font-semibold mb-4 text-center">Fishbone</p>
          <PlaybookImage 
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/working-conflict/fishbone.png" 
            alt="Fishbone diagram example" 
            className="rounded-lg border border-gray-700/50 mb-4"
          />
          <div className="flex justify-center">
            <ToolCallout
              tool="miro"
              label="Fishbone diagram [miro template]"
              href="https://miro.com/templates/fishbone-diagram/"
              description="A template for identifying multiple causes"
            />
          </div>
        </div>

        <ol className="list-decimal pl-6 space-y-6 text-gray-300 mb-6" start="3">
          <li>
            <p>After the workshop: create an anonymous mini survey to capture anything anyone was afraid to say during the group workshop.</p>
            <ExpandableCallout type="info" title="Tip" className="mt-2">
              <p className="text-gray-300">
                I find this useful to help less loud voices be heard, and also frankly because sometimes it is a problem created by the leader/VP and that can be difficult to address safely in some cultures.
              </p>
            </ExpandableCallout>
          </li>
          <li>
            <p>Repeat steps 1-3 with the other team</p>
          </li>
        </ol>
      </PlaybookCard>

      {/* Co-create solution */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Co-create solution aka 'Honesty Day'
        </h3>
        <p className="text-gray-300 mb-6">
          Ok so you've now got intimate knowledge on the problem from both team's perspective
        </p>
        <p className="text-gray-300 mb-6">
          Nice work!
        </p>
        <p className="text-gray-300 mb-6">
          Now it's time to:
        </p>
        <p className="text-gray-300 mb-6">
          Bring the 2 leaders back together.
        </p>
        <p className="text-gray-300 mb-6">
          Here's what you need to do:
        </p>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Share a summary of the problem and the root cause(s) their teams' identified (async memo so they can absorb the insights)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Get them on a call and dig in to:</span>
          </li>
        </ul>

        <ul className="space-y-3 mb-6 pl-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">Whats really behind this</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">What can they do about it as leaders (sticky note solutions)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">What timeframe are they gonna work to fix it</span>
          </li>
        </ul>

        <ExpandableCallout type="info" title="Tip" className="mb-6">
          <p className="text-gray-300 mb-2">
            in my experience it tends to be one of the following:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">Goals and expectations</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">Toxic individual (#brilliantJerk)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">How they incentivise their teams</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">Their behaviour towards one another</span>
            </li>
          </ul>
          <p className="text-gray-300 mt-2">
            Sometimes this requires escalation to CEO/Founder or the mutual team they share.
          </p>
        </ExpandableCallout>

        <p className="text-gray-300 mb-6">
          Whatever the agreement is (which will depend on the root cause) after this call the flow to follow facilitate is
        </p>

        <p className="text-gray-300 mb-6 font-medium">
          Leaders publish commitment (together) → We make it happen → We measure results
        </p>

        <p className="text-gray-300 mb-2 font-medium">
          //example:
        </p>

        <p className="text-gray-300 mb-4">
          Let's say it was an issue with misaligned goals / incentives and the VPs/leaders acknowledged on the call this was something solvable. The next steps would be
        </p>

        <ol className="list-decimal pl-6 space-y-4 text-gray-300 mb-6">
          <li>
            <p>Comms from leaders: "Hey folks, thanks for sharing your thoughts when we dug in to the problems facing both our teams (root cause analysis). It's clear that we've got some work to do over the next few months to improve how we work together but one thing that we can change immediately is our OKRs. Moving forwards we are going to share xyz objectives and key results so that we win or lose together as a untied GTM function. Here's how we're going to go about that…"</p>
          </li>
          <li>
            <p>Co-create new aligned OKRs</p>
          </li>
          <li>
            <p>Retrospective after 4-12 weeks to check in on the root cause - do we still see the symptoms happening?</p>
          </li>
        </ol>

        <h4 className="text-lg font-semibold text-white mb-3">
          ✅ Outcome
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">You acknowledged the problem</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">You found the root cause</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">You co-created a solution, leaning into the leaders closest to the change</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">The teams were included and engaged</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">The plan to fix was transparent</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">The action was framed as a measurable experiment</span>
          </li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default WorkingThroughConflictStage2; 